import translate from "@insite/client-framework/Translate";
import Clickable from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import React, { ChangeEvent } from "react";
import styled, { css } from "styled-components";

export const QuantityWrapper = styled.div`
    display: flex;
    align-items: center;
    position: relative;
    width: 100%;
    min-width: 100px;
`;

export const QuantityButton = styled(Clickable)`
    position: absolute;
    z-index: 1;
    top: calc(50% - 8px);

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    span[class^="IconWrapper-sc-"] {
        color: ${({ theme }) => theme.colors.primary.main};
    }
`;

export const MinusButton = styled(QuantityButton)`
    left: 10px;
`;

export const PlusButton = styled(QuantityButton)`
    right: 10px;
`;

export const quantityFieldStyles: TextFieldPresentationProps = {
    cssOverrides: {
        formField: css`
            margin-bottom: 0;
            flex: 1;
        `,
        formInputWrapper: css`
            margin-top: 0 !important;

            input {
                text-align: center;
                padding: 0 !important;

                &::-webkit-outer-spin-button,
                &::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }

                &:focus {
                    border-left: none;
                    border-right: none;
                }
            }
        `,
        inputSelect: css`
            -moz-appearance: textfield;
        `,
    },
    labelProps: {
        css: css`
            display: none;
        `,
    },
};

interface ProductQuantityWrapperProps {
    value: number | string;
    onChange: (value: number) => void;
    /** Raw event handler for text input changes. If provided, used instead of the internal handler that calls onChange. */
    onTextChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    onBlur?: () => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    min?: number;
    max?: number;
    disabled?: boolean;
    label?: string;
    dataTestSelector?: string;
    extendedStyles?: TextFieldPresentationProps;
    /** Additional props forwarded directly to the underlying TextField. */
    textFieldProps?: Partial<TextFieldProps>;
}

const ProductQuantityWrapper: React.FC<ProductQuantityWrapperProps> = ({
    value,
    onChange,
    onTextChange,
    onBlur,
    onKeyDown,
    min = 0,
    max = 999999,
    disabled = false,
    label,
    dataTestSelector,
    extendedStyles,
    textFieldProps,
}) => {
    const numericValue = typeof value === "string" ? parseFloat(value) : value;
    const currentValue = Number.isNaN(numericValue) ? 0 : numericValue;

    const handleIncrement = () => {
        if (currentValue < max) {
            onChange(currentValue + 1);
        }
    };

    const handleDecrement = () => {
        if (currentValue > min) {
            onChange(currentValue - 1);
        }
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const parsed = parseInt(event.currentTarget.value, 10);
        if (!Number.isNaN(parsed)) {
            onChange(parsed);
        }
    };

    return (
        <QuantityWrapper 
            onBlur={onBlur} 
            onKeyDown={onKeyDown}>
            <MinusButton
                onClick={handleDecrement}
                disabled={disabled || currentValue <= min}
                data-test-selector={dataTestSelector ? `${dataTestSelector}_decrement` : "decrementQuantity"}
            >
                <Icon src={Minus} size={17} />
            </MinusButton>
            <TextField
                {...textFieldProps}
                {...extendedStyles}
                {...quantityFieldStyles}
                label={label || translate("QTY")}
                value={value}
                min={min}
                max={max}
                disabled={disabled}
                onChange={onTextChange || handleChange}
                data-test-selector={dataTestSelector || "quantityInput"}
            />
            <PlusButton
                onClick={handleIncrement}
                disabled={disabled || currentValue >= max}
                data-test-selector={dataTestSelector ? `${dataTestSelector}_increment` : "incrementQuantity"}
            >
                <Icon src={Plus} size={17} />
            </PlusButton>
        </QuantityWrapper>
    );
};

export default ProductQuantityWrapper;
